import React, { useState } from 'react';
import { 
  ChevronDown, 
  ChevronRight, 
  FileText, 
  User, 
  Mail, 
  Phone, 
  Globe, 
  Palette, 
  Image, 
  Settings,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Edit,
  ArrowRight,
  Bell,
  Eye,
  X,
  Plus
} from 'lucide-react';
import { ProjectData, NotificationRecipient } from '../types';
import { CustomerUpdateModal } from './CustomerUpdateModal';
import { AdminFieldEditor } from './AdminFieldEditor';
import { JiraStatusAdvancer } from './JiraStatusAdvancer';
import { NotificationManager } from './NotificationManager';

const JIRA_BROWSE_BASE = 'https://teamideal.atlassian.net/browse';

interface ProjectDetailsProps {
  project: ProjectData;
  triggerCustomerUpdateModal?: boolean;
  onCustomerUpdateModalClose?: () => void;
}

export function ProjectDetails({ project, triggerCustomerUpdateModal, onCustomerUpdateModalClose }: ProjectDetailsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCustomerUpdateModalOpen, setIsCustomerUpdateModalOpen] = useState(false);
  const [isAdminFieldEditorOpen, setIsAdminFieldEditorOpen] = useState(false);
  const [isJiraStatusAdvancerOpen, setIsJiraStatusAdvancerOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationRecipient[]>(project?.notifications || []);
  const [adminWatchers, setAdminWatchers] = useState<string[]>(project?.adminWatchers || []);
  const [newWatcherEmail, setNewWatcherEmail] = useState('');

  // Handle external trigger for customer update modal
  React.useEffect(() => {
    if (triggerCustomerUpdateModal) {
      setIsCustomerUpdateModalOpen(true);
      onCustomerUpdateModalClose?.();
    }
  }, [triggerCustomerUpdateModal, onCustomerUpdateModalClose]);

  // Handle notifications change
  const handleNotificationsChange = async (updatedNotifications: NotificationRecipient[]) => {
    setNotifications(updatedNotifications);
    
    // Save to backend
    try {
      const response = await fetch(`https://us-central1-ima-onboard.cloudfunctions.net/updateProject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jiraKey: project.jiraKey,
          updates: {
            notifications: updatedNotifications
          }
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update notifications');
      }
      
      console.log('✅ Notifications updated successfully');
    } catch (error) {
      console.error('Error updating notifications:', error);
      alert('Failed to save notifications. Please try again.');
    }
  };

  // Handle admin watchers
  const handleAddWatcher = async () => {
    if (!newWatcherEmail || !newWatcherEmail.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }
    
    if (adminWatchers.includes(newWatcherEmail)) {
      alert('This email is already in the watchers list');
      return;
    }
    
    const updatedWatchers = [...adminWatchers, newWatcherEmail];
    setAdminWatchers(updatedWatchers);
    setNewWatcherEmail('');
    
    // Save to backend
    try {
      const response = await fetch(`https://us-central1-ima-onboard.cloudfunctions.net/updateProject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jiraKey: project.jiraKey,
          updates: {
            adminWatchers: updatedWatchers
          }
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update admin watchers');
      }
      
      console.log('✅ Admin watcher added successfully');
    } catch (error) {
      console.error('Error updating admin watchers:', error);
      alert('Failed to save admin watcher. Please try again.');
    }
  };

  const handleRemoveWatcher = async (email: string) => {
    const updatedWatchers = adminWatchers.filter(w => w !== email);
    setAdminWatchers(updatedWatchers);
    
    // Save to backend
    try {
      const response = await fetch(`https://us-central1-ima-onboard.cloudfunctions.net/updateProject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jiraKey: project.jiraKey,
          updates: {
            adminWatchers: updatedWatchers
          }
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update admin watchers');
      }
      
      console.log('✅ Admin watcher removed successfully');
    } catch (error) {
      console.error('Error updating admin watchers:', error);
      alert('Failed to remove admin watcher. Please try again.');
    }
  };

  const formatValue = (value: any, fieldKey?: string) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-gray-400 italic">Not provided</span>;
    }
    if (typeof value === 'boolean') {
      return value ? (
        <span className="inline-flex items-center text-green-600">
          <CheckCircle className="h-4 w-4 mr-1" />
          Yes
        </span>
      ) : (
        <span className="inline-flex items-center text-red-600">
          <XCircle className="h-4 w-4 mr-1" />
          No
        </span>
      );
    }
    if (typeof value === 'string' && value.startsWith('http')) {
      return (
        <a 
          href={value} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {value}
        </a>
      );
    }
    
    // Special formatting for phone number with country code
    if (fieldKey === 'notificationPhone' && project.formData.notificationPhoneCountryCode) {
      return (
        <span className="text-gray-900">
          <span className="font-mono bg-gray-100 px-2 py-1 rounded text-sm">
            {project.formData.notificationPhoneCountryCode} {value}
          </span>
        </span>
      );
    }
    
    return <span className="text-gray-900">{value}</span>;
  };

  const getCompletionStatus = (fieldKey: string, value: any) => {
    // Check if field is conditionally required
    if (fieldKey === 'appIconUrl' && project.formData.useLogoAsIcon) {
      // If using logo as icon, app icon is not required
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
    
    if (fieldKey === 'redirectWebsiteUrl' && project.formData.ecommerceType === 'integrated') {
      // If using integrated shop, redirect URL is not required
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
    
    if (fieldKey === 'notificationEmail' && !project.formData.enableEmailNotifications) {
      // If email notifications disabled, email is not required
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
    
    if (fieldKey === 'notificationPhone' && !project.formData.enableSmsNotifications) {
      // If SMS notifications disabled, phone is not required
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
    
    // Optional fields that should show warning instead of error
    const optionalFields = [
      'companyDescription',
      'copyrightMsg',
      'useWebsiteForShop',
      'appleAccountInvitesSection', // This is a section header, not a data field
      'useLogoAsIcon', // This is a UI control, not a required field
      'googlePlayInternalTestLink', // IMA-14: staff-set in Jira later, not customer-required
    ];
    
    if (optionalFields.includes(fieldKey)) {
      // Optional fields show warning if empty, success if filled
      if (value === null || value === undefined || value === '') {
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      }
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
    
    // Required fields show error if empty
    if (value === null || value === undefined || value === '') {
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    }
    
    return <CheckCircle className="h-4 w-4 text-green-500" />;
  };

  const formSections = [
    {
      title: 'Basic App Information',
      icon: FileText,
      fields: [
        { key: 'appName', label: 'App Name', icon: FileText },
        { key: 'companyDescription', label: 'Company Description', icon: FileText },
        { key: 'copyrightMsg', label: 'Copyright Message', icon: FileText },
      ]
    },
    {
      title: 'Contact Information',
      icon: User,
      fields: [
        { key: 'appStoreContactEmail', label: 'App Store Contact Email', icon: Mail },
        { key: 'appStoreContactPhone', label: 'App Store Contact Phone', icon: Phone },
        { key: 'notificationEmail', label: 'Notification Email', icon: Mail },
        { key: 'notificationPhoneCountryCode', label: 'Notification Phone Country Code', icon: Phone },
        { key: 'notificationPhone', label: 'Notification Phone', icon: Phone },
      ]
    },
    {
      title: 'Visual Assets',
      icon: Image,
      fields: [
        { key: 'logoUrl', label: 'Logo URL', icon: Image },
        { key: 'appIconUrl', label: 'App Icon URL', icon: Image },
        { key: 'useLogoAsIcon', label: 'Use Logo as Icon', icon: Settings },
        { key: 'primaryColor', label: 'Primary Color', icon: Palette },
        { key: 'secondaryColor', label: 'Secondary Color', icon: Palette },
      ]
    },
    {
      title: 'Website & Legal',
      icon: Globe,
      fields: [
        { key: 'ecommerceType', label: 'E-commerce Solution', icon: Settings },
        { key: 'redirectWebsiteUrl', label: 'Redirect Website URL', icon: Globe },
        { key: 'termsUrl', label: 'Terms of Service URL', icon: Globe },
        { key: 'privacyUrl', label: 'Privacy Policy URL', icon: Globe },
      ]
    },
    {
      title: 'VersiRent Configuration',
      icon: Settings,
      fields: [
        { key: 'versiRentUsername', label: 'VersiRent Username', icon: User },
        { key: 'versiRentPassword', label: 'VersiRent Password', icon: Settings },
        { key: 'versiRentActiveAgreement', label: 'Active Agreement', icon: CheckCircle },
        { key: 'testerEmails', label: 'Tester Emails', icon: Mail },
        { key: 'googlePlayInternalTestLink', label: 'Google Play Internal Test Link', icon: Globe },
      ]
    },
    {
      title: 'Apple Developer Account',
      icon: Settings,
      fields: [
        { key: 'appleDeveloperAccountName', label: 'Apple Developer Account Name', icon: User },
        { key: 'appleTeamId', label: 'Apple Team ID', icon: Settings },
        { key: 'appleAccountInvitesSection', label: 'Apple Account Invites Section', icon: Settings },
        { key: 'appleUserMwhorey', label: 'Apple User (mwhorey)', icon: User },
        { key: 'appleUserNmore', label: 'Apple User (nmore)', icon: User },
        { key: 'appleUserDev', label: 'Apple User (dev)', icon: User },
        { key: 'appleUserCstack', label: 'Apple User (cstack)', icon: User },
        { key: 'appleUserDscarafiotti', label: 'Apple User (dscarafiotti)', icon: User },
      ]
    },
    {
      title: 'Additional Settings',
      icon: Settings,
      fields: [
        { key: 'teamKeyEnabled', label: 'Team Key Enabled', icon: Settings },
        { key: 'googlePlayBrandAssetAgreement', label: 'Google Play Brand Asset Agreement', icon: FileText },
        { key: 'enableEmailNotifications', label: 'Enable Email Notifications', icon: Settings },
        { key: 'enableSmsNotifications', label: 'Enable SMS Notifications', icon: Settings },
      ]
    }
  ];

  // Calculate completion statistics with conditional logic
  const isFieldCompleted = (fieldKey: string, value: any) => {
    // Check if field is conditionally required
    if (fieldKey === 'appIconUrl' && project.formData.useLogoAsIcon) {
      // If using logo as icon, app icon is not required
      return true;
    }
    
    if (fieldKey === 'redirectWebsiteUrl' && project.formData.ecommerceType === 'integrated') {
      // If using integrated shop, redirect URL is not required
      return true;
    }
    
    if (fieldKey === 'notificationEmail' && !project.formData.enableEmailNotifications) {
      // If email notifications disabled, email is not required
      return true;
    }
    
    if (fieldKey === 'notificationPhone' && !project.formData.enableSmsNotifications) {
      // If SMS notifications disabled, phone is not required
      return true;
    }
    
    // Optional fields are always considered "completed" for percentage calculation
    const optionalFields = [
      'companyDescription',
      'copyrightMsg',
      'useWebsiteForShop',
      'appleAccountInvitesSection', // This is a section header, not a data field
      'googlePlayInternalTestLink', // IMA-14: staff-set in Jira later, not customer-required
    ];
    
    if (optionalFields.includes(fieldKey)) {
      return true; // Optional fields don't count against completion
    }
    
    // Required fields must have values
    if (value === null || value === undefined || value === '') {
      return false;
    }
    
    return true;
  };

  // Calculate total required fields (excluding optional fields)
  const optionalFields = [
    'companyDescription',
    'copyrightMsg', 
    'useWebsiteForShop',
    'appleAccountInvitesSection',
    'googlePlayInternalTestLink', // IMA-14: staff-set in Jira later, not customer-required
  ];
  
  const totalRequiredFields = formSections.reduce((acc, section) => {
    return acc + section.fields.filter(field => !optionalFields.includes(field.key)).length;
  }, 0);
  
  const completedRequiredFields = formSections.reduce((acc, section) => {
    return acc + section.fields.filter(field => {
      if (optionalFields.includes(field.key)) return false; // Skip optional fields
      const value = project.formData[field.key as keyof typeof project.formData];
      return isFieldCompleted(field.key, value);
    }).length;
  }, 0);
  
  const completionPercentage = totalRequiredFields > 0 ? Math.round((completedRequiredFields / totalRequiredFields) * 100) : 0;

  return (
    <div className="border-t border-gray-200 bg-gray-50">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {isExpanded ? (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronRight className="h-5 w-5 text-gray-400" />
            )}
            <div>
              <h4 className="text-sm font-medium text-gray-900 flex items-center">
                {project.jiraKey.startsWith('PENDING-') ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 mr-2">
                    {project.jiraKey}
                  </span>
                ) : (
                  <a
                    href={`${JIRA_BROWSE_BASE}/${project.jiraKey}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2 hover:bg-blue-200 hover:underline"
                    title={`Open ${project.jiraKey} in Jira`}
                  >
                    {project.jiraKey}
                  </a>
                )}
                Project Details
              </h4>
              <p className="text-xs text-gray-500">
                Form completion: {completedRequiredFields}/{totalRequiredFields} required fields ({completionPercentage}%)
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="flex items-center text-xs text-gray-500">
              <Clock className="h-3 w-3 mr-1" />
              Created: {new Date(project.createdAt).toLocaleDateString()}
            </div>
            <div className="flex items-center text-xs text-gray-500">
              <Clock className="h-3 w-3 mr-1" />
              Updated: {new Date(project.updatedAt).toLocaleDateString()}
            </div>
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="px-6 pb-6 bg-white border-l-4 border-l-blue-500">
          <div className="space-y-6">
            {/* Project Metadata */}
            <div className="bg-white p-4 rounded-lg border">
              <div className="flex items-center justify-between mb-3">
                <h5 className="text-sm font-medium text-gray-900 flex items-center">
                  <Settings className="h-4 w-4 mr-2" />
                  Project Metadata
                </h5>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setIsCustomerUpdateModalOpen(true)}
                    className="flex items-center space-x-1 px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                  >
                    <Edit className="h-3 w-3" />
                    <span>Update Customer</span>
                  </button>
                  <button
                    onClick={() => setIsAdminFieldEditorOpen(true)}
                    className="flex items-center space-x-1 px-3 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                  >
                    <Settings className="h-3 w-3" />
                    <span>Admin Fields</span>
                  </button>
                  <button
                    onClick={() => setIsJiraStatusAdvancerOpen(true)}
                    className="flex items-center space-x-1 px-3 py-1 text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1"
                  >
                    <ArrowRight className="h-3 w-3" />
                    <span>Advance Status</span>
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-600">Jira Key:</span>
                  {project.jiraKey.startsWith('PENDING-') ? (
                    <span className="ml-2 text-gray-900">{project.jiraKey}</span>
                  ) : (
                    <a
                      href={`${JIRA_BROWSE_BASE}/${project.jiraKey}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 text-blue-600 hover:text-blue-800 hover:underline font-medium"
                      title={`Open ${project.jiraKey} in Jira`}
                    >
                      {project.jiraKey}
                    </a>
                  )}
                </div>
                <div>
                  <span className="font-medium text-gray-600">App Type:</span>
                  <span className="ml-2 text-gray-900 capitalize">{project.appType}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Status:</span>
                  <span className="ml-2 text-gray-900">{project.status}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Customer:</span>
                  <span className="ml-2 text-gray-900">{project.customerName}</span>
                </div>
                <div className="md:col-span-2">
                  <span className="font-medium text-gray-600">Email:</span>
                  <span className="ml-2 text-gray-900">{project.customerEmail}</span>
                </div>
              </div>
            </div>

            {/* Project Notifications Section */}
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h5 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                <Bell className="h-4 w-4 mr-2 text-blue-600" />
                <span className="text-blue-900">Project Notifications</span>
                <span className="ml-2 text-xs font-normal text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  Customer & Stakeholders
                </span>
                <span className="ml-2 text-xs text-gray-500">
                  ({notifications.filter(n => n.enabled).length} active)
                </span>
              </h5>
              <div className="text-sm text-gray-600 mb-4">
                <strong>Customer & stakeholder notifications:</strong> These recipients will receive status updates, alerts, and project communications. Add the customer's team members, project managers, or other stakeholders who should be kept informed.
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 text-xs text-blue-800">
                <strong>💡 Note:</strong> For internal admin monitoring (staff oversight), use the "Admin Watchers" feature which is managed separately.
              </div>
              <NotificationManager
                notifications={notifications}
                onChange={handleNotificationsChange}
              />
            </div>

            {/* Admin Watchers Section */}
            <div className="bg-white p-4 rounded-lg border border-purple-200">
              <h5 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                <Eye className="h-4 w-4 mr-2 text-purple-600" />
                <span className="text-purple-900">Admin Watchers</span>
                <span className="ml-2 text-xs font-normal text-purple-600 bg-purple-50 px-2 py-1 rounded">
                  Internal Staff Only
                </span>
                <span className="ml-2 text-xs text-gray-500">
                  ({adminWatchers.length} watching)
                </span>
              </h5>
              <div className="text-sm text-gray-600 mb-4">
                <strong>Internal monitoring:</strong> Admin watchers receive ALL project notifications regardless of customer settings. Use this for project managers, QA team, or support staff who need visibility into all project activity.
              </div>
              
              {/* Add Watcher Form */}
              <div className="flex gap-2 mb-4">
                <input
                  type="email"
                  value={newWatcherEmail}
                  onChange={(e) => setNewWatcherEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddWatcher()}
                  placeholder="admin@idealss.com"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <button
                  onClick={handleAddWatcher}
                  className="flex items-center gap-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm font-medium"
                >
                  <Plus className="h-4 w-4" />
                  Add Watcher
                </button>
              </div>

              {/* Watchers List */}
              {adminWatchers.length > 0 ? (
                <div className="space-y-2">
                  {adminWatchers.map((email, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-md">
                      <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4 text-purple-600" />
                        <span className="text-sm font-medium text-gray-900">{email}</span>
                      </div>
                      <button
                        onClick={() => handleRemoveWatcher(email)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                        title="Remove watcher"
                      >
                        <X className="h-3 w-3" />
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 bg-gray-50 border-2 border-dashed border-gray-300 rounded-md">
                  <Eye className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No admin watchers configured</p>
                  <p className="text-xs text-gray-400 mt-1">Add staff members who should monitor this project</p>
                </div>
              )}
            </div>

            {/* Form Data Sections */}
            {formSections.map((section, sectionIndex) => {
              const SectionIcon = section.icon;
              // Visibility must use real values — optional empty fields are "completed"
              // for scoring and would otherwise keep empty sections (e.g. VersiRent) visible.
              const hasAnyFieldValue = section.fields.some((field) => {
                const value = project.formData[field.key as keyof typeof project.formData];
                if (value === null || value === undefined || value === '') return false;
                if (Array.isArray(value) && value.length === 0) return false;
                return true;
              });

              if (!hasAnyFieldValue) {
                return null; // Don't show empty sections
              }
              
              // Calculate section completion excluding optional fields
              const sectionRequiredFields = section.fields.filter(field => !optionalFields.includes(field.key));
              const sectionCompletedFields = sectionRequiredFields.filter(field => {
                const value = project.formData[field.key as keyof typeof project.formData];
                return isFieldCompleted(field.key, value);
              });

              return (
                <div key={sectionIndex} className="bg-white p-4 rounded-lg border">
                  <h5 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                    <SectionIcon className="h-4 w-4 mr-2" />
                    {section.title}
                    <span className="ml-2 text-xs text-gray-500">
                      ({sectionCompletedFields.length}/{sectionRequiredFields.length} required completed)
                    </span>
                  </h5>
                  <div className="space-y-3">
                    {section.fields.map((field) => {
                      const value = project.formData[field.key as keyof typeof project.formData];
                      const FieldIcon = field.icon;
                      
                      return (
                        <div key={field.key} className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-0.5">
                            {getCompletionStatus(field.key, value)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <FieldIcon className="h-4 w-4 text-gray-400" />
                              <span className="text-sm font-medium text-gray-600">
                                {field.label}:
                              </span>
                            </div>
                            <div className="mt-1 text-sm">
                              {formatValue(value, field.key)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Support Contact Information */}
            {project.supportContact && (
              <div className="bg-white p-4 rounded-lg border">
                <h5 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                  <User className="h-4 w-4 mr-2" />
                  Support Contact
                </h5>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="font-medium text-gray-600">Email:</span>
                    <span className="text-gray-900">{project.supportContact.email}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="font-medium text-gray-600">Phone:</span>
                    <span className="text-gray-900">{project.supportContact.phone}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="font-medium text-gray-600">Hours:</span>
                    <span className="text-gray-900">{project.supportContact.hours}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Change Request Information */}
            {project.changeRequest && project.changeRequest.requested && (
              <div className="bg-white p-4 rounded-lg border border-orange-200">
                <h5 className="text-sm font-medium text-orange-900 mb-3 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Change Request
                </h5>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-gray-600">Requested:</span>
                    <span className="ml-2 text-orange-600">Yes</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Details:</span>
                    <p className="mt-1 text-gray-900">{project.changeRequest.details}</p>
                  </div>
                  {project.changeRequest.timestamp && (
                    <div>
                      <span className="font-medium text-gray-600">Timestamp:</span>
                      <span className="ml-2 text-gray-900">
                        {new Date(project.changeRequest.timestamp).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Customer Update Modal */}
      <CustomerUpdateModal
        project={project}
        isOpen={isCustomerUpdateModalOpen}
        onClose={() => setIsCustomerUpdateModalOpen(false)}
        onUpdate={() => {
          // This will trigger a refresh of the project data
          // The parent component should handle this
          window.location.reload();
        }}
      />

      {/* Admin Field Editor Modal */}
      <AdminFieldEditor
        project={project}
        isOpen={isAdminFieldEditorOpen}
        onClose={() => setIsAdminFieldEditorOpen(false)}
        onSave={async (fields) => {
          try {
            const response = await fetch('https://us-central1-ima-onboard.cloudfunctions.net/updateJiraFields', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                jiraKey: project.jiraKey,
                fields
              }),
            });

            if (!response.ok) {
              throw new Error('Failed to update Jira fields');
            }

            const result = await response.json();
            console.log('Jira fields updated:', result);
            
            // Refresh the project data
            window.location.reload();
          } catch (error) {
            console.error('Error updating Jira fields:', error);
            throw error;
          }
        }}
      />

      {/* Jira Status Advancer Modal */}
      <JiraStatusAdvancer
        project={project}
        isOpen={isJiraStatusAdvancerOpen}
        onClose={() => setIsJiraStatusAdvancerOpen(false)}
        onAdvance={async (newStatus) => {
          try {
            const response = await fetch('https://us-central1-ima-onboard.cloudfunctions.net/advanceJiraStatus', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                jiraKey: project.jiraKey,
                newStatus
              }),
            });

            if (!response.ok) {
              throw new Error('Failed to advance Jira status');
            }

            const result = await response.json();
            console.log('Jira status advanced:', result);
            
            // Refresh the project data
            window.location.reload();
          } catch (error) {
            console.error('Error advancing Jira status:', error);
            throw error;
          }
        }}
      />
    </div>
  );
}
